import type { Camera } from '../../services/camerasApi';
import type { Line } from '../../services/linesApi';
import type { Policy } from '../../services/policiesApi';
import type { Zone } from '../../services/zonesApi';
import { buildPolicyTroubleshootingView, type PolicyBucket } from '../../utils/policyTroubleshooting';
import { buildPolicyScopeChips, buildPolicyScopeSummary, type PolicyScopeLookups } from './policyScope';
import {
  GENERIC_POLICY_TYPE_LABELS,
  PACK_BY_KEY,
  PACK_DISPLAY_ORDER,
  getPackKeyForAlias,
  getPolicyAliasDefinition,
  getPolicyLabelFromAliasOrType,
  getPolicySubjectFromEntityTypes,
  readPolicyUiContext,
  resolvePackAndAlias,
  type PackKey,
} from './policyPacks';

export type PolicyPresentationLookups = PolicyScopeLookups & {
  cameras?: Camera[];
  zones?: Zone[];
  lines?: Line[];
};

export type PresentedPolicy = {
  packKey: PackKey;
  packLabel: string;
  alias: string;
  policyLabel: string;
  cameraLabel: string;
  scopeSummary: string;
  scopeChips: string[];
  statusBucket: PolicyBucket;
  technicalSummary?: string;
  source: Policy;
};

function fallbackStatusBucket(policy: Policy): PolicyBucket {
  if (policy.status !== 'active' || policy.enabled === false) return 'draft';
  if (policy.deployment_state === 'DEPLOYED') return 'healthy';
  if (policy.deployment_state === 'DEPLOYING') return 'deploying';
  if (policy.deployment_state === 'FAILED' || policy.deployment_state === 'FAILED_TIMEOUT') return 'attention';
  return 'attention';
}

function buildCameraLabel(policy: Policy, lookups: PolicyPresentationLookups): string {
  const targetCameraId = String(policy.target?.camera_id || policy.camera_id || policy.scope?.camera_ids?.[0] || '').trim();
  const targetDeviceId = String(policy.target?.edge_device_id || policy.edge_device_id || policy.scope?.edge_device_id || '').trim();
  const camera = (lookups.cameras || []).find(
    (item) =>
      String(item.camera_id || '').trim() === targetCameraId &&
      (!targetDeviceId || String(item.edge_device_id || '').trim() === targetDeviceId),
  );

  if (camera) return String(camera.name || camera.camera_id).trim();
  if (targetCameraId && targetDeviceId) return `${targetDeviceId} / ${targetCameraId}`;
  if (targetCameraId) return targetCameraId;
  return 'No target camera';
}

export function derivePolicyAlias(policy: Policy, lookups: PolicyPresentationLookups = {}): string {
  const uiContext = readPolicyUiContext(policy);
  const persistedAlias = String(uiContext?.policy_alias || '').trim();
  if (persistedAlias && getPolicyAliasDefinition(persistedAlias)) {
    return persistedAlias;
  }

  return resolvePackAndAlias({
    policyType: policy.policy_type,
    subject: getPolicySubjectFromEntityTypes(policy.scope?.entity_types),
    zoneIds: policy.scope?.zone_ids,
    zonesById: lookups.zonesById,
    uiContext,
  }).alias;
}

export function derivePackIdentity(policy: Policy, lookups: PolicyPresentationLookups = {}): PackKey {
  const uiContext = readPolicyUiContext(policy);
  const persistedAlias = String(uiContext?.policy_alias || '').trim();
  if (persistedAlias && getPolicyAliasDefinition(persistedAlias)) {
    return getPackKeyForAlias(persistedAlias);
  }

  const persistedPack = String(uiContext?.pack_key || '').trim() as PackKey;
  if (persistedPack && PACK_BY_KEY[persistedPack]) {
    return persistedPack;
  }

  return resolvePackAndAlias({
    policyType: policy.policy_type,
    subject: getPolicySubjectFromEntityTypes(policy.scope?.entity_types),
    zoneIds: policy.scope?.zone_ids,
    zonesById: lookups.zonesById,
    uiContext,
  }).packKey;
}

export function deriveStatusBucket(policy: Policy, lookups: PolicyPresentationLookups = {}): PolicyBucket {
  if (lookups.cameras && lookups.zones && lookups.lines) {
    return buildPolicyTroubleshootingView({
      policy,
      cameras: lookups.cameras,
      zones: lookups.zones,
      lines: lookups.lines,
    }).bucket;
  }

  return fallbackStatusBucket(policy);
}

export function presentPolicy(policy: Policy, lookups: PolicyPresentationLookups = {}): PresentedPolicy {
  const alias = derivePolicyAlias(policy, lookups);
  const packKey = derivePackIdentity(policy, lookups);
  const aliasDefinition = getPolicyAliasDefinition(alias);
  const policyLabel = getPolicyLabelFromAliasOrType({ alias, policyType: policy.policy_type });
  const entityTypes = Array.isArray(policy.scope?.entity_types)
    ? policy.scope?.entity_types.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  const technicalSummary = [
    GENERIC_POLICY_TYPE_LABELS[policy.policy_type] || policy.policy_type,
    entityTypes.length > 0 ? `entities: ${entityTypes.join(', ')}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return {
    packKey,
    packLabel: PACK_BY_KEY[packKey]?.label || 'Policies',
    alias,
    policyLabel: aliasDefinition?.label || policyLabel,
    cameraLabel: buildCameraLabel(policy, lookups),
    scopeSummary: buildPolicyScopeSummary(policy, lookups, 2),
    scopeChips: buildPolicyScopeChips(policy, lookups, 2),
    statusBucket: deriveStatusBucket(policy, lookups),
    technicalSummary,
    source: policy,
  };
}

export function groupPresentedPoliciesByPack(policies: PresentedPolicy[]): Record<PackKey, PresentedPolicy[]> {
  const grouped = PACK_DISPLAY_ORDER.reduce<Record<PackKey, PresentedPolicy[]>>((acc, packKey) => {
    acc[packKey] = [];
    return acc;
  }, {} as Record<PackKey, PresentedPolicy[]>);

  policies.forEach((policy) => {
    grouped[policy.packKey] = grouped[policy.packKey] || [];
    grouped[policy.packKey].push(policy);
  });

  return grouped;
}
